import { useEffect } from 'react';
import { Toaster as SonnerToaster, toast, useSonner } from 'sonner';
import { isInternalPath } from '@/shared/lib/safe-path';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { takeToastHref, toastIdClass, toastIdFromNode } from './toast-link';
import styles from './Toaster.module.css';

/**
 * shadcn-style Toaster поверх Sonner. Монтируется один раз в AppProviders;
 * тосты показываются через `toast(...)` из 'sonner' (реэкспорт в shared/ui).
 * Цвета — через CSS-переменные Luma; тему (light/dark) прокидывает app-слой.
 *
 * Поведение: по центру сверху, без кнопки закрытия. Клик по тосту закрывает
 * его; если тосту привязан href (см. setToastHref) — вместо закрытия ведёт
 * на этот маршрут. При наведении таймер приостанавливается (дефолт Sonner).
 *
 * @use хост тостов, монтируется один раз в провайдерах; сами тосты — `toast()` из sonner.
 */
export function Toaster({ theme = 'light' }: { theme?: 'light' | 'dark' }) {
  const isNarrow = useMediaQuery('(max-width: 640px)');
  const { toasts } = useSonner();

  useEffect(() => {
    for (const t of toasts) {
      const mark = toastIdClass(t.id);
      if (!mark || t.className?.split(' ').includes(mark)) continue;
      toast(t.title, { id: t.id, className: t.className ? `${t.className} ${mark}` : mark });
    }
  }, [toasts]);

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a')) return;
    const li = target.closest<HTMLElement>('[data-sonner-toast]');
    if (!li) return;
    const id = toastIdFromNode(li);
    if (id === undefined) return;
    const href = takeToastHref(id);
    toast.dismiss(id);
    if (!href) return;
    if (isInternalPath(href)) {
      void import('@/app/router').then(({ router }) => router.navigate(href));
    } else {
      window.location.href = href;
    }
  };

  return (
    <div onClick={handleClick}>
      <SonnerToaster
        theme={theme}
        position="top-center"
        duration={5000}
        offset={16}
        gap={8}
        visibleToasts={isNarrow ? 1 : 3}
        toastOptions={{
          unstyled: true,
          classNames: {
            toast: styles.toast,
            content: styles.content,
            title: styles.title,
            description: styles.description,
            icon: styles.icon,
            actionButton: styles.actionButton,
            cancelButton: styles.cancelButton,
          },
        }}
      />
    </div>
  );
}
